import { createAction, props } from '@ngrx/store';
import { CreateProgramDto, ProgramDto, UpdateProgramDto } from '@shared/dto';
import { PaginatedListQuery } from '@shared/interfaces/paginated-list-query.interface';

// Read list
export const readPrograms = createAction('@app/program/read', (query?: PaginatedListQuery) => ({ query }));
export const readProgramsSuccess = createAction('@app/program/read/success', props<{ data: ProgramDto[]; total: number }>());
export const readProgramsFailure = createAction('@app/program/read/failure', props<{ error: any }>());

// Read single (detail) — resolves from the loaded list by id, falling back to a
// server fetch (readOne) so a reload/deep-link always has the full, fresh detail.
export const readProgram = createAction('@app/program/read-one', props<{ programId: number }>());
export const readProgramSuccess = createAction('@app/program/read-one/success', props<{ program: ProgramDto }>());
export const readProgramFailure = createAction('@app/program/read-one/failure', props<{ error: any }>());

// Create
export const createProgram = createAction('@app/program/create', props<{ dto: CreateProgramDto }>());
export const createProgramSuccess = createAction('@app/program/create/success', props<{ program: ProgramDto }>());
export const createProgramFailure = createAction('@app/program/create/failure', props<{ error: any }>());

// Update
export const updateProgram = createAction('@app/program/update', props<{ programId: number; dto: UpdateProgramDto }>());
export const updateProgramSuccess = createAction('@app/program/update/success', props<{ program: Partial<ProgramDto> }>());
export const updateProgramFailure = createAction('@app/program/update/failure', props<{ error: any }>());
// 428 (PROGRAM.UPDATE_REQUIRES_CONFIRMATION): the update edits a shared TEMPLATE session.
// Not a failure — the page shows the clone dialog and resends the SAME `dto` with
// `strategy: clone` + a copy title on the affected slots. `message` is the backend's
// (localized) reason, carried verbatim so the dialog can quote it.
export const updateProgramRequiresConfirmation = createAction(
  '@app/program/update/requires-confirmation',
  props<{ programId: number; dto: UpdateProgramDto; message: string }>(),
);

// Delete — `confirm` resends as `?confirm=true` after the 428 (active assignments) is accepted.
export const deleteProgram = createAction('@app/program/delete', props<{ programId: number; confirm?: boolean }>());
export const deleteProgramSuccess = createAction('@app/program/delete/success', props<{ program: ProgramDto }>());
export const deleteProgramFailure = createAction('@app/program/delete/failure', props<{ error: any }>());
// 428: the therapy still has ACTIVE assignments. The delete is safe (they froze their
// snapshot) but deliberate — the page confirms with the backend message and resends `confirm`.
export const deleteProgramRequiresConfirmation = createAction(
  '@app/program/delete/requires-confirmation',
  props<{ programId: number; message: string }>(),
);

// Restore
export const restoreProgram = createAction('@app/program/restore', props<{ programId: number }>());
export const restoreProgramSuccess = createAction('@app/program/restore/success', props<{ program: ProgramDto }>());
export const restoreProgramFailure = createAction('@app/program/restore/failure', props<{ error: any }>());
